
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Card } from "@/components/ui/card";
import CampManagedBanner from "@/components/common/CampManagedBanner";
import { CampLocation } from "@/types/campLocation";
import { UserInvitation } from "@/types/userInvitation";
import { useAuth } from "@/context/AuthContext";
import { useLocationsByOrganization } from "@/hooks/useLocationsByOrganization";
import { LoadingState, ErrorState, NoLocationsState } from "./components/LoadingStates";
import LocationEditingHeader from "./components/LocationEditingHeader";
import LocationEditingForm from "./components/LocationEditingForm";
import { toast } from "sonner";
import { db } from "@/services/firebaseService";
import { doc, getDoc } from "firebase/firestore";
import { 
  getLatestLocationVersion
} from "@/services/locationVersion/versionStatusService";
import {
  getCachedLocationVersionHistory
} from "@/services/locationVersion/locationVersionCacheService";
import { useOrganizationData } from "@/hooks/useOrganizationData";
import { getCampOrganization } from "@/services/campOrganizationQuery";
import { createLogger } from "@/utils/logUtils";
import { getEffectiveLocationAccessGroupIdForWelcome } from "@/utils/welcomeLocationAccessGroup";
import { UnsavedChangesDialog } from "@/components/admin/locations/components/UnsavedChangesDialog";
import { isLocationEditLiveSource } from "@/config/locationEditFlag";
import { FieldStateProvider } from "@/services/campDataFieldState";
import { resolveAudience } from "@/services/campDataAudience";

const logger = createLogger("WelcomeLocationEditingState");

interface LocationEditingStateProps {
  invitation: UserInvitation | null;
}

export const LocationEditingState: React.FC<LocationEditingStateProps> = ({ invitation }) => {
  const { currentUser, userProfile, userRoles, isAdmin } = useAuth();
  
  // Use ref to track mounting state and prevent excessive renders
  const isMounted = useRef(false);
  const [isReady, setIsReady] = useState(false);
  
  // Track when location data was last updated (used to refresh when programs are added/edited/deleted)
  const [locationUpdateTimestamp, setLocationUpdateTimestamp] = useState<number>(Date.now());
  
  // Add state to track the latest draft version
  const [latestDraft, setLatestDraft] = useState<any>(null);
  
  // Add state for organization data
  const [organizationData, setOrganizationData] = useState<any>(null);
  
  // Add state for organization ID from custom claims (for anonymous users)
  const [claimsOrgId, setClaimsOrgId] = useState<string | null>(null);
  const [claimsGroupId, setClaimsGroupId] = useState<string | null>(null);
  
  // State to persist the active tab across re-renders
  const [activeTab, setActiveTab] = useState("details");

  // Track form dirty state to guard navigation between locations
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const pendingLocationRef = useRef<CampLocation | null>(null);
  const saveHandlerRef = useRef<(() => Promise<void>) | null>(null);
  
  // Extract organization ID from invitation, user profile, or custom claims
  const extractOrgId = (invitation: UserInvitation | null): string | null => {
    if (invitation?.organization) {
      // Get from invitation if available
      return invitation.organization.id;
    }
    
    // For authenticated users without tokens, get from user profile
    if (currentUser && userProfile?.organizationId) {
      return userProfile.organizationId;
    }
    
    // For anonymous users, get from custom claims
    if (currentUser?.isAnonymous && claimsOrgId) {
      return claimsOrgId;
    }
    
    return null;
  };
  
  // Org + location group from custom claims for anonymous users (token / grantInviteClaims flows)
  useEffect(() => {
    if (!currentUser?.isAnonymous) {
      setClaimsOrgId(null);
      setClaimsGroupId(null);
      return;
    }
    currentUser
      .getIdTokenResult()
      .then((tokenResult) => {
        const orgId = tokenResult.claims.organizationId as string;
        if (orgId) {
          setClaimsOrgId(orgId);
        }
        const gid = (tokenResult.claims as { locationAccessGroupId?: string }).locationAccessGroupId;
        setClaimsGroupId(typeof gid === "string" ? gid : null);
      })
      .catch((error) => {
        console.error("Error getting custom claims:", error);
      });
  }, [currentUser]);
  
  const organizationId = extractOrgId(invitation);

  /** Token-driven invite: scope from invitation only (missing field = full org for this link). No invite: profile then claims. */
  const effectiveLocationAccessGroupId = useMemo(
    () => getEffectiveLocationAccessGroupIdForWelcome(invitation, userProfile, claimsGroupId),
    [invitation, userProfile, claimsGroupId]
  );
  
  logger.info("Using organization ID:", organizationId, "from invitation:", invitation?.id || "none (authenticated user)");
  
  // Use our hook for organization data
  const { 
    organizations, 
    isLoading: isLoadingOrgs, 
    error: orgsError,
    refreshOrganizationData  // Use this to manually refresh org data
  } = useOrganizationData();

  // Fetch organization data directly if needed
  useEffect(() => {
    const fetchOrganizationData = async () => {
      try {
        // First try to get from our hook result
        const orgFromHook = organizations.find(org => org.id === organizationId);
        
        if (orgFromHook) {
          logger.info("Found organization from hook:", orgFromHook.name);
          logger.debug("Organization data:", {
            id: orgFromHook.id,
            name: orgFromHook.name,
            descAboutTheOrg: orgFromHook.descAboutTheOrg?.substring(0, 20) || 'missing',
            descStaffingAndLeadership: orgFromHook.descStaffingAndLeadership?.substring(0, 20) || 'missing'
          });
          setOrganizationData(orgFromHook);
          return;
        }
        
        // Fallback to direct fetch
        logger.info("Organization not found in hook data, fetching directly...");
        const org = await getCampOrganization(db, organizationId);
        logger.info("Fetched organization directly:", org?.name);
        
        if (org) {
          logger.debug("Direct fetch organization data:", {
            id: org.id,
            name: org.name,
            descAboutTheOrg: org.descAboutTheOrg?.substring(0, 20) || 'missing',
            descStaffingAndLeadership: org.descStaffingAndLeadership?.substring(0, 20) || 'missing'
          });
          setOrganizationData(org);
        } else {
          logger.error("Could not fetch organization data with ID:", organizationId);
          toast.error("Could not load organization information");
        }
      } catch (error) {
        logger.error("Error fetching organization data:", error);
        toast.error("Error loading organization information");
      }
    };
    
    if (organizationId) {
      fetchOrganizationData();
    } else {
      logger.error("No organization ID available to fetch data");
    }
  }, [organizationId, organizations]);
  
  // Use our hook for location data (invitation group overrides signed-in user's unrelated group)
  const { locations, currentLocation, selectLocation, isLoading, isFetchCompleted, refreshLocations } = useLocationsByOrganization(organizationId, {
    locationAccessGroupId: effectiveLocationAccessGroupId,
    restrictToWelcomeWorkflowStatuses: true,
  });

  // Prefer invitation defaultLocationId on initial load and persist to localStorage
  useEffect(() => {
    try {
      if (!isLoading && isFetchCompleted && locations && locations.length > 0) {
        const preferredId = invitation?.defaultLocationId;
        if (preferredId) {
          const target = locations.find(l => l.id === preferredId);
          if (target && (!currentLocation || currentLocation.id !== target.id)) {
            selectLocation(target);
            // Store for subsequent visits
            try { localStorage.setItem('kb_default_location_id', preferredId); } catch (_err) { /* no-op */ }
          }
        }
      }
    } catch (e) {
      // Non-fatal: default selection is best-effort
    }
  }, [isLoading, isFetchCompleted, locations, invitation?.defaultLocationId, currentLocation, selectLocation]);

  // Persist defaultLocationId to localStorage as soon as available (independent of locations fetch)
  useEffect(() => {
    const preferredId = invitation?.defaultLocationId;
    if (preferredId) {
      try { localStorage.setItem('kb_default_location_id', preferredId); } catch (_err) { /* no-op */ }
    }
  }, [invitation?.defaultLocationId]);

  // Wrap selection with unsaved-changes guard
  const guardedSelectLocation = (next: CampLocation) => {
    if (!next || next.id === currentLocation?.id) return;
    if (isFormDirty) {
      pendingLocationRef.current = next;
      setShowUnsavedDialog(true);
      return;
    }
    selectLocation(next);
  };

  const handleDiscardChanges = () => {
    setShowUnsavedDialog(false);
    const next = pendingLocationRef.current;
    pendingLocationRef.current = null;
    if (next) {
      selectLocation(next);
    }
  };

  const handleSaveChanges = async () => {
    setShowUnsavedDialog(false);
    try {
      if (saveHandlerRef.current) {
        await saveHandlerRef.current();
      }
    } catch (e) {
      // Save failed; do not navigate
      return;
    }
    const next = pendingLocationRef.current;
    pendingLocationRef.current = null;
    if (next) {
      selectLocation(next);
    }
  };
  
  // Track location data availability
  const [locationForEdit, setLocationForEdit] = useState<CampLocation | null>(null);
  
  // Function to fetch the latest draft version
  const fetchLatestDraftVersion = async (locationId: string) => {
    // KB-3308: live-source mode renders the live doc directly — no draft version is fetched or
    // merged. Returning null routes loadLatestDraft to setLocationForEdit(currentLocation).
    if (isLocationEditLiveSource()) return null;
    try {
      logger.info("Fetching latest draft version for location:", locationId);
      const latestVersion = await getLatestLocationVersion(db, locationId);
      
      if (latestVersion) {
        logger.info("Found latest draft version:", latestVersion.versionId);
        setLatestDraft(latestVersion);
        return latestVersion;
      } else {
        logger.info("No latest draft version found");
        return null;
      }
    } catch (error) {
      logger.error("Error fetching latest draft version:", error);
      toast.error("Error loading the latest draft version");
      return null;
    }
  };
  
  // Function to merge location with latest draft data
  const mergeLocationWithLatestDraft = (baseLocation: CampLocation, draftVersion: any) => {
    if (!draftVersion || !draftVersion.data) return baseLocation;
    
    logger.info("Merging location with latest draft data");
    
    // Create a new object with base location data
    const mergedLocation = {
      ...baseLocation,
      // Overlay with latest draft data
      ...draftVersion.data,
      // Preserve the original ID
      id: baseLocation.id,
      // Include version information
      latestVersionId: draftVersion.versionId
    };
    
    logger.debug("Merged location data:", 
      { id: mergedLocation.id, name: mergedLocation.name, latestVersionId: draftVersion.versionId }
    );
    
    return mergedLocation;
  };
  
  // Function to refresh organization data
  const refreshOrgData = async () => {
    logger.info("Manually refreshing organization data");
    
    try {
      // First invalidate the React Query cache
      await refreshOrganizationData();
      
      // Then do a direct fetch for immediate update
      if (organizationId) {
        const freshOrgData = await getCampOrganization(db, organizationId);
        if (freshOrgData) {
          logger.info("Fresh organization data fetched:", {
            name: freshOrgData.name,
            descAboutTheOrgLength: freshOrgData.descAboutTheOrg?.length || 0,
            descStaffingAndLeadershipLength: freshOrgData.descStaffingAndLeadership?.length || 0,
            descAboutTheOrgSample: freshOrgData.descAboutTheOrg?.substring(0, 20),
            descStaffingAndLeadershipSample: freshOrgData.descStaffingAndLeadership?.substring(0, 20)
          });
          setOrganizationData(freshOrgData);
          return freshOrgData;
        } else {
          logger.error("Failed to fetch fresh organization data");
        }
      }
      } catch (error) {
        logger.error("Error refreshing organization data:", error);
      }
    return null;
  };
  
  // Initialize component state when we have location data
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }

    logger.debug("Locations data updated:", 
      locations?.length, 
      "Current location:", 
      currentLocation?.id || "none",
      "Loading:", isLoading,
      "Ready:", isReady,
      "Fetch completed:", isFetchCompleted
    );

    // Only update the location for edit when we have a valid location and fetch is complete
    if (currentLocation && !isLoading && isFetchCompleted) {
      // Fetch the latest draft version when we have a current location
      const loadLatestDraft = async () => {
        const latestDraftVersion = await fetchLatestDraftVersion(currentLocation.id);
        
        if (latestDraftVersion) {
          // Merge the current location with the latest draft data
          const mergedLocation = mergeLocationWithLatestDraft(currentLocation, latestDraftVersion);
          logger.info("Setting merged location with draft data for edit");
          setLocationForEdit(mergedLocation);
        } else {
          logger.info("Setting regular location data for edit (no draft)");
          setLocationForEdit(currentLocation);
        }
        
        setIsReady(true);
      };
      
      loadLatestDraft();
    }
  }, [currentLocation, isLoading, isFetchCompleted, locations]);
  
  // Add effect to listen for custom events to refresh content
  useEffect(() => {
    const handleLocationVersionChanged = () => {
      logger.info("Detected version changed event, refreshing location data");
      
      if (refreshLocations) {
        refreshLocations();
        
        // Also refresh the latest draft version if we have a current location
        if (currentLocation) {
          fetchLatestDraftVersion(currentLocation.id);
        }
        
        setLocationUpdateTimestamp(Date.now());
      }
    };
    
    // Handle specific event for refreshing draft after save
    const handleRefreshWelcomeLatestDraft = (event: any) => {
      const { locationId, latestDraft } = event.detail || {};
      
      logger.info("Received refreshWelcomeLatestDraft event for location:", locationId);
      
      if (locationId && currentLocation && locationId === currentLocation.id) {
        logger.info("Refreshing with latest draft:", latestDraft?.versionId);
        
        if (latestDraft) {
          setLatestDraft(latestDraft);
          
          // Merge the latest draft with the current location
          const mergedLocation = mergeLocationWithLatestDraft(currentLocation, latestDraft);
          logger.info("Updating location with fresh draft data after save");
          setLocationForEdit(mergedLocation);
        } else {
          // If no latest draft was provided, fetch it
          fetchLatestDraftVersion(locationId).then(fetchedDraft => {
            if (fetchedDraft && currentLocation) {
              const mergedLocation = mergeLocationWithLatestDraft(currentLocation, fetchedDraft);
              setLocationForEdit(mergedLocation);
            }
          });
        }
      }
    };
    
    // New handler for organization data saved event
    const handleOrganizationDataSaved = () => {
      logger.info("Organization data saved event received, refreshing org data");
      refreshOrgData();
    };
    
    // Listen for both version changes and location changes
    window.addEventListener("locationVersionChanged", handleLocationVersionChanged);
    window.addEventListener("versionchange", handleLocationVersionChanged);
    window.addEventListener("refreshVersionHistory", handleLocationVersionChanged);
    window.addEventListener("refreshWelcomeLatestDraft", handleRefreshWelcomeLatestDraft as EventListener);
    window.addEventListener("organizationDataSaved", handleOrganizationDataSaved);
    
    return () => {
      window.removeEventListener("locationVersionChanged", handleLocationVersionChanged);
      window.removeEventListener("versionchange", handleLocationVersionChanged);
      window.removeEventListener("refreshVersionHistory", handleLocationVersionChanged);
      window.removeEventListener("refreshWelcomeLatestDraft", handleRefreshWelcomeLatestDraft as EventListener);
      window.removeEventListener("organizationDataSaved", handleOrganizationDataSaved);
    };
  }, [refreshLocations, currentLocation]);
  
  // Notify parent (WelcomePage) about workflow status so it can render a top-of-page banner
  useEffect(() => {
    try {
      const status = locationForEdit?.workflowStatus || null;
      window.dispatchEvent(new CustomEvent("campManagedWorkflowStatusUpdated", { detail: { workflowStatus: status } }));
    } catch (e) {
      logger.error("Error dispatching campManagedWorkflowStatusUpdated event", e);
    }
  }, [locationForEdit?.workflowStatus]);
  
  // Add error state for missing organization ID - moved after hooks
  if (!organizationId) {
    logger.error("No valid organization ID found in invitation");
    return <ErrorState message="Could not determine organization ID from invitation. Please contact support." />;
  }
  
  // Render loading state
  if (isLoading || !isFetchCompleted) {
    return <LoadingState />;
  }
  
  // Render no locations message
  if (!locations || locations.length === 0) {
    return <NoLocationsState />;
  }
  
  // If locations are loaded but currentLocation isn't selected yet, allow the defaulting effect to run
  if (!currentLocation) {
    logger.debug("No current location selected yet; waiting for default selection effect");
    return <LoadingState message="Preparing editing interface..." />;
  }
  
  // Don't render form content until we're ready
  if (!isReady || !locationForEdit) {
    return <LoadingState message="Preparing editing interface..." />;
  }
  
  // Add error logging for debugging purposes
  logger.debug("Rendering form with location:", {
    id: locationForEdit.id,
    name: locationForEdit.name,
    organizationId: organizationId,
    invitationToken: invitation?.token || null,
    updateTimestamp: locationUpdateTimestamp,
    latestVersionId: locationForEdit.latestVersionId || 'none',
    organizationData: organizationData ? {
      id: organizationData.id,
      name: organizationData.name,
      descAboutTheOrg: organizationData.descAboutTheOrg ? 'present' : 'missing',
      descStaffingAndLeadership: organizationData.descStaffingAndLeadership ? 'present' : 'missing'
    } : 'missing'
  });
  
  return (
    <>
      <Card className="w-full max-w-4xl mx-auto">
      <LocationEditingHeader 
        location={locationForEdit} 
        locations={locations} 
        onLocationSelect={guardedSelectLocation}
      />
      
      {/* KB-3318: expose per-field ownership to the welcome form's governed fields for the DIRECTOR
          audience (or null → no badge, while the role resolves). Keyed on the location id so switching
          locations re-reads the state doc. Admin previewers resolve to "staff". The AgeGroupVariation
          modal mounts its own provider (a portal), so it resolves its own audience — see that file. */}
      <FieldStateProvider
        kind="campLocation"
        id={locationForEdit.id}
        audience={resolveAudience({ userProfile, userRoles, isAdmin, invitation })}
        currentUid={currentUser?.uid ?? null}
      >
        <LocationEditingForm
          location={locationForEdit}
          invitation={invitation}
          latestDraft={latestDraft}
          organizationData={organizationData}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onDirtyChange={setIsFormDirty}
          registerSaveHandler={(fn) => { saveHandlerRef.current = fn; }}
        />
      </FieldStateProvider>
      </Card>

      {/* Unsaved changes when switching locations */}
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onOpenChange={setShowUnsavedDialog}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveChanges}
      />
    </>
  );
};
